// Presentation only: amounts are supplied by the engine or the recorded version.
export const financialTotals = (source = {}) => ({
  totalHT: source.totalHT ?? source.totalSellingPriceHT ?? source.total_net_ht ?? source.net_selling_price_ht,
  totalVat: source.totalVat ?? source.totalVatAmount ?? source.total_tax ?? source.tax,
  totalTTC: source.totalTTC ?? source.totalSellingPriceTTC ?? source.total_ttc,
});
export const taxRows = (source = {}) => source.taxBreakdown ?? source.tax_breakdown ?? [];
export const missingTaxNote = 'Ventilation de TVA non disponible pour ce calcul enregistré.';

const cents = (value) => {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) {
    throw new Error('Source financière incomplète.');
  }
  return Math.round(Number(value) * 100);
};
const complete = (totals) => Object.values(totals).every((value) => value !== undefined && value !== null);
const equalTotals = (left, right) => Object.keys(left).every((key) => cents(left[key]) === cents(right[key]));

// Check cents and source consistency, never calculate a tax from a rate.
export const assertFinancialPresentation = (source = {}, recordedTotals) => {
  const totals = financialTotals(source.totals || source);
  if (!complete(totals)) throw new Error('Totaux financiers indisponibles.');
  if (cents(totals.totalHT) + cents(totals.totalVat) !== cents(totals.totalTTC)) {
    throw new Error('Totaux financiers incohérents.');
  }
  for (const mirror of [source.calculation, source.salesSheet, recordedTotals]) {
    if (!mirror) continue;
    const other = financialTotals(mirror);
    if (complete(other) && !equalTotals(totals, other)) throw new Error('Mélange incohérent de sources financières.');
  }
  const rows = taxRows(source);
  if (rows.length) {
    const sum = { totalHT: 0, totalVat: 0, totalTTC: 0 };
    rows.forEach((row) => {
      const ht = cents(row.base_ht), vat = cents(row.tax_amount), ttc = cents(row.total_ttc);
      if (ht + vat !== ttc) throw new Error('Ventilation de TVA incohérente.');
      sum.totalHT += ht; sum.totalVat += vat; sum.totalTTC += ttc;
    });
    if (!Object.keys(sum).every((key) => sum[key] === cents(totals[key]))) throw new Error('Ventilation et totaux financiers incohérents.');
  }
  (source.optionViews || []).forEach((option) => assertFinancialPresentation(option));
  return totals;
};

export const taxBreakdownHtml = (source, formatMoney) => {
  const rows = taxRows(source);
  if (!rows.length) return `<p class="muted">${missingTaxNote}</p>`;
  return `<table class="vat" style="break-inside:avoid"><thead><tr><th>Taux</th><th>HT</th><th>TVA</th><th>TTC</th></tr></thead><tbody>${rows.map((row) => `<tr><td>${Number(row.rate) * 100} %</td><td>${formatMoney(row.base_ht)}</td><td>${formatMoney(row.tax_amount)}</td><td>${formatMoney(row.total_ttc)}</td></tr>`).join('')}</tbody></table>`;
};

export const optionFinancialHtml = (source, formatMoney) => source.optionTotalsUnavailable
  ? '<p>Totaux des options non disponibles dans la version enregistrée.</p>'
  : (source.optionViews || []).map((option, index) => `
  <section style="break-inside:avoid"><h3>Option ${index + 1} — exclue du devis initial</h3>
  ${taxBreakdownHtml(option, formatMoney)}
  <p>Total option HT : ${formatMoney(option.totals.totalHT)}</p>
  <p>TVA option : ${formatMoney(option.totals.totalVat)}</p>
  <p>Total option TTC : ${formatMoney(option.totals.totalTTC)}</p></section>`).join('');
